/**
 * Edge-middleware Supabase client wired against Next.js's request/response
 * cookies. The three apps' `proxy.ts` files all need the same boilerplate
 * — wire `request.cookies.getAll()` to read, and write to BOTH
 * `request.cookies` (so subsequent reads in the same middleware see the
 * fresh values) AND `response.cookies` (so the browser actually receives
 * them). This helper hides that adapter so middlewares stay focused on
 * routing logic.
 *
 * Cookies set here pass through `applyCookieDomain()` so the session is
 * visible across `*.modernatc.com` subdomains in production.
 */

import { createServerClient as createSsrClient } from "@supabase/ssr";
import type { NextRequest, NextResponse } from "next/server";
import type { Database } from "./types";
import { readPublicEnv } from "./env";
import { applyCookieDomain } from "./cookies";
import type { CookieToSet } from "./server";

export interface MiddlewareClientResult {
  /** RLS-aware Supabase client bound to the request's cookies. */
  supabase: ReturnType<typeof createSsrClient<Database>>;
  /**
   * The same NextResponse you passed in. Returned for symmetry — Next's
   * middleware contract is "build a response, return it" and chaining
   * keeps that ergonomic.
   */
  response: NextResponse;
}

export function createMiddlewareClient(
  request: NextRequest,
  response: NextResponse,
): MiddlewareClientResult {
  const env = readPublicEnv();
  const supabase = createSsrClient<Database>(
    env.SUPABASE_URL,
    env.SUPABASE_ANON_KEY,
    {
      cookies: {
        getAll: () =>
          request.cookies.getAll().map((c) => ({ name: c.name, value: c.value })),
        setAll: (cookies: CookieToSet[]) => {
          for (const { name, value, options } of applyCookieDomain(cookies)) {
            // Mirror to request.cookies so any subsequent read in this
            // same middleware invocation sees the just-set value (e.g.
            // refreshSession() then getUser() in the same handler).
            request.cookies.set(name, value);
            response.cookies.set(name, value, options as Parameters<typeof response.cookies.set>[2]);
          }
        },
      },
    },
  );
  return { supabase, response };
}
